import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Project } from '../types';
import ScrollToTop from '../components/ScrollToTop';

const PrefabBuilding: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // 设置浏览器标签标题
  useEffect(() => {
    document.title = '装配式建筑 - 东大国际';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectsRes = await api.get('/projects');
        setProjects(projectsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredProjects = projects.filter(project => {
    // 过滤没有图片的项目
    if (!project.image_url || project.image_url.trim() === '') {
      return false;
    }
    
    // 检查是否为装配式建筑项目 - 从不同可能的位置读取special_category
    const specialCategory = 
      project.special_category || 
      (project.categories && typeof project.categories === 'object' && project.categories.special_category) ||
      (project.categories && typeof project.categories === 'string' && 
        (() => {
          try {
            const parsed = JSON.parse(project.categories);
            return parsed.special_category;
          } catch (e) {
            return null;
          }
        })()
      );
    
    // 检查特殊分类是否包含装配式建筑（支持数组或字符串）
    if (Array.isArray(specialCategory)) {
      return specialCategory.includes('装配式建筑');
    } else {
      return specialCategory === '装配式建筑';
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="max-w-[2000px] mx-auto px-4 md:px-12 py-12">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900 mb-4"></div>
              <p className="text-gray-600 font-medium">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[80px] min-h-screen bg-gray-50 font-PingFang">
      <ScrollToTop />
      <section className="py-10 bg-white">
        <div className="max-w-[2000px] mx-auto px-4 md:px-12 py-12">
          {/* 标题和介绍 */}
          

          {/* 标题和视图切换 */}
          <div className="relative mb-24">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold">
                {i18n.language === 'zh' ? '装配式建筑项目' : 'Prefabricated Building Projects'}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="absolute -bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
          </div>

          {/* 项目展示 */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {filteredProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group block bg-white"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={i18n.language === 'zh' ? project.title : project.title_en}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {i18n.language === 'zh' ? project.title : project.title_en}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {i18n.language === 'zh' ? project.description : project.description_en}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group block py-5 border-b border-gray-100"
                >
                  <div className="flex justify-between items-center">
                    <div className="w-48">
                      <h3 className="text-base font-medium text-gray-900">
                        {i18n.language === 'zh' ? project.title : project.title_en}
                      </h3>
                    </div>
                    <div className="flex-1 px-12">
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {i18n.language === 'zh' ? project.description : project.description_en}
                      </p>
                    </div>
                    <div className="w-24 text-right">
                      <span className="text-sm text-gray-500 border-b border-gray-300 pb-0.5">
                        {i18n.language === 'zh' ? '查看详情' : 'View Details'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* 无结果提示 */}
          {filteredProjects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-gray-500 text-lg mb-4">
                {i18n.language === 'zh' ? '暂无装配式建筑项目' : 'No prefabricated building projects available'}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PrefabBuilding; 